import { useEffect, useMemo, useRef, useState } from 'react';
import {
  flattenBlocks,
  formatTimer,
  LAYOUT_PRESETS,
  makeMessage,
  parseScript,
  PRODUCT_NAME,
  ProjectSchema,
  QUICK_CUES,
  reconcileIds,
  serializeScript,
  timerValueMs,
} from '@goanna-im/shared';
import type {
  DisplaySettings,
  InfoMessage,
  InfoMessageType,
  LayoutPresetId,
  LayoutSet,
  Project,
  ScrollControl,
  ScrollState,
  SessionState,
  SettingsUpdate,
  TimerControl,
  Timers,
} from '@goanna-im/shared';
import { useSession } from '../lib/useSession.js';
import { ConnectionBadge } from '../components/ConnectionBadge.js';
import { RoomCode } from '../components/RoomCode.js';
import { VoiceFollowPanel } from './VoiceFollowPanel.js';
import { CloudLibraryPanel } from './CloudLibraryPanel.js';
import { ProducerTimer } from './ProducerTimer.js';
import { AiScriptPanel } from '../components/AiScriptPanel.js';
import { ScriptMirror } from '../components/ScriptMirror.js';
import { RecordingToggle, ScriptHistory, EndShowPanel } from './ShowPanels.js';
import { getSocket } from '../lib/socket.js';
import { logout } from '../lib/auth.js';
import type { AuthUser } from '../lib/auth.js';
import {
  deleteAllLocalData,
  listProjects,
  saveProject,
} from '../lib/db.js';

export function ProducerRoute({
  user,
  navigate,
  onLogout,
}: {
  user: AuthUser;
  navigate: (to: string) => void;
  onLogout: () => void;
}) {
  const { state, actions } = useSession();
  if (!state.joined || !state.session) {
    return (
      <ProducerStart
        user={user}
        navigate={navigate}
        onLogout={onLogout}
        onCreate={() => actions.createSession('producer', user.displayName)}
        onJoin={(code) => actions.joinSession(code, 'producer', user.displayName)}
        error={state.error}
      />
    );
  }
  return (
    <ProducerConsole state={state} actions={actions} user={user} navigate={navigate} onLogout={onLogout} />
  );
}

function AuthHeaderControls({
  user,
  navigate,
  onLogout,
}: {
  user: AuthUser;
  navigate: (to: string) => void;
  onLogout: () => void;
}) {
  return (
    <div className="row">
      <span className="tag">{user.displayName}</span>
      {user.role === 'admin' && <button onClick={() => navigate('/admin')}>Admin</button>}
      <button
        onClick={async () => {
          await logout();
          onLogout();
          navigate('/');
        }}
      >
        Sign out
      </button>
    </div>
  );
}

function ProducerStart({
  user,
  navigate,
  onLogout,
  onCreate,
  onJoin,
  error,
}: {
  user: AuthUser;
  navigate: (to: string) => void;
  onLogout: () => void;
  onCreate: () => void;
  onJoin: (code: string) => void;
  error: string | null;
}) {
  const params = new URLSearchParams(window.location.search);
  const [code, setCode] = useState((params.get('room') ?? '').toUpperCase());
  const [busy, setBusy] = useState(false);

  return (
    <div className="wrap stack" style={{ paddingTop: '2rem', maxWidth: 560 }}>
      <header className="row" style={{ justifyContent: 'space-between' }}>
        <h1 className="brand" style={{ margin: 0 }}>
          {PRODUCT_NAME}
          <small>Producer</small>
        </h1>
        <AuthHeaderControls user={user} navigate={navigate} onLogout={onLogout} />
      </header>

      <section className="card stack">
        <h3 style={{ marginTop: 0 }}>Start a session</h3>
        <p className="muted" style={{ margin: 0 }}>
          Create a new room, then share the code (or QR) with your talent's tablet.
        </p>
        <button
          className="primary"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            onCreate();
          }}
        >
          {busy ? 'Starting…' : 'Start new session'}
        </button>
      </section>

      <section className="card stack">
        <h3 style={{ marginTop: 0 }}>Rejoin a session</h3>
        <div className="row">
          <input
            aria-label="Room code"
            placeholder="Existing room code"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            type="text"
            autoComplete="off"
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            style={{ flex: 1 }}
          />
          <button disabled={code.length < 3} onClick={() => onJoin(code)}>
            Rejoin
          </button>
        </div>
      </section>
      {error && <p style={{ color: 'var(--urgent)' }}>{error}</p>}
    </div>
  );
}

function ProducerConsole({
  state,
  actions,
  user,
  navigate,
  onLogout,
}: {
  state: ReturnType<typeof useSession>['state'];
  actions: ReturnType<typeof useSession>['actions'];
  user: AuthUser;
  navigate: (to: string) => void;
  onLogout: () => void;
}) {
  const session = state.session!;
  const { project, scroll, layout, settings, timers, message, presence, roomCode } = session;

  // The ScriptEditor loads its text once on mount; bumping this key remounts it so an
  // externally-applied script (AI apply, cloud/local load) actually shows.
  const [editorKey, setEditorKey] = useState(0);
  const [applied, setApplied] = useState<Project | null>(null);

  // Replace the whole script from an external source (AI apply, cloud/local load):
  // push to the room AND remount the editor so the new text actually appears.
  const loadIntoEditor = (p: Project) => {
    actions.updateScript(p);
    setApplied(p);
    setEditorKey((k) => k + 1);
  };

  return (
    <div className="wrap stack">
      <header className="row" style={{ justifyContent: 'space-between', paddingTop: '0.5rem' }}>
        <div className="brand">
          {PRODUCT_NAME} <small style={{ display: 'inline', color: 'var(--muted)' }}>Producer</small>
        </div>
        <div className="row">
          <span className="tag">Room {roomCode}</span>
          <ConnectionBadge connected={state.connected} presence={presence} />
          <AuthHeaderControls user={user} navigate={navigate} onLogout={onLogout} />
        </div>
      </header>

      <AssistConsent roomCode={roomCode} />

      <div className="producer-body">
        {/* Top utility strip: Share/QR · Production timer · Other timers */}
        <div className="prod-strip">
          <section className="card stack">
            <h3 style={{ marginTop: 0 }}>Share with talent</h3>
            <RoomCode roomCode={roomCode} />
            <p className="muted" style={{ fontSize: '0.8rem', margin: 0 }}>
              {(() => {
                if (!session.expiresAt)
                  return 'Presenter access: the 12-hour window starts when talent first connects — safe to prepare ahead of time.';
                const ms = Math.max(0, session.expiresAt - Date.now());
                if (ms <= 0) return 'Presenter access has closed — start a new session to share again.';
                const mins = Math.floor(ms / 60000);
                const h = Math.floor(mins / 60);
                const m = mins % 60;
                return `Presenter access closes in ${h > 0 ? `${h}h ${m}m` : `${m}m`} (12-hour limit).`;
              })()}
            </p>
          </section>
          <ProducerTimer />
          <TimersPanel timers={timers} onTimer={actions.timer} />
        </div>

        {/* The hero. Left: a long Script editor with the AI helpers beneath it.
            Right: Presenter view with the live controls stacked under it. */}
        <div className="prod-main">
          <div className="stack">
            <ScriptEditor grow key={editorKey} project={applied ?? project} onChange={actions.updateScript} />
            <AiScriptPanel currentProject={project} onApply={loadIntoEditor} />
          </div>
          <div className="stack">
            <PresenterViewPanel
              project={project}
              anchorBlockId={scroll.anchor.blockId}
              onJump={(blockId) => actions.scroll({ action: 'goto', blockId })}
            />
            <Transport scroll={scroll} project={project} onScroll={actions.scroll} />
            <CuePad onCue={(m) => actions.showCue(m)} onClear={actions.clearCue} activeMessage={message?.body ?? null} />
            <MessageComposer onCue={(m) => actions.showCue(m)} onClear={actions.clearCue} />
          </div>
        </div>

        {/* Everything else, tightly packed (masonry — no stretch gaps) */}
        <div className="prod-tools">
          <RecordingToggle recording={session.recording} onToggle={actions.setRecording} />
          <ScriptHistory listHistory={actions.listHistory} restoreHistory={actions.restoreHistory} />
          <CloudLibraryPanel project={project} onLoad={loadIntoEditor} />
          <ProjectsPanel project={project} onLoad={loadIntoEditor} />
          <LayoutPanel currentPreset={layout.presetId} onLayout={(presetId) => actions.setLayout({ presetId })} />
          <DisplayPanel settings={settings} onSettings={actions.updateSettings} />
          <VoiceFollowPanel project={project} onScroll={actions.scroll} anchorBlockId={scroll.anchor.blockId} />
          <AiPanel project={project} onCue={(m) => actions.showCue(m)} />
        </div>

        {/* Full-width footer */}
        <EndShowPanel onSignoff={actions.signoff} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Script editor — plain text, parsed live, with stable block ids so the reading
// position is preserved on the presenter while editing.
// ---------------------------------------------------------------------------
function ScriptEditor({
  project,
  onChange,
  grow = false,
}: {
  project: Project;
  onChange: (p: Project) => void;
  /** Fill the parent column (flex:1) so the editor runs as long as the column beside it. */
  grow?: boolean;
}) {
  const [text, setText] = useState(() => serializeScript(project));
  const [title, setTitle] = useState(project.title);
  const prevRef = useRef<Project>(project);
  const initialised = useRef(false);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load the incoming script once (single-producer authority owns edits after that).
  useEffect(() => {
    if (!initialised.current) {
      setText(serializeScript(project));
      setTitle(project.title);
      prevRef.current = project;
      initialised.current = true;
    }
  }, [project]);

  const push = (nextText: string, nextTitle: string) => {
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => {
      const parsed = parseScript(nextText, nextTitle || 'Untitled script');
      const reconciled = reconcileIds(prevRef.current, parsed);
      reconciled.version = (prevRef.current.version ?? 0) + 1;
      reconciled.modifiedAt = Date.now();
      prevRef.current = reconciled;
      onChange(reconciled);
    }, 140);
  };

  return (
    <section className="card stack" style={grow ? { flex: 1, minHeight: 0 } : undefined}>
      <h3>Script</h3>
      <input
        aria-label="Script title"
        value={title}
        onChange={(e) => {
          setTitle(e.target.value);
          push(text, e.target.value);
        }}
      />
      <textarea
        className="script-editor"
        aria-label="Script text"
        value={text}
        spellCheck
        onChange={(e) => {
          setText(e.target.value);
          push(e.target.value, title);
        }}
      />
      <p className="muted" style={{ margin: 0, fontSize: '0.85rem' }}>
        Use <code># Heading</code>, <code>[PAUSE]</code>, <code>[CAMERA 2]</code>,{' '}
        <code>[VIDEO: …]</code>. Lines ending in ? become questions. Edits appear live on the
        presenter without a refresh.
      </p>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Consent to super-admin assist. The producer sees a request to take control and
// Allows or Denies it; while control is held, a banner offers Revoke. Nothing an
// admin does reaches the show until the producer has Allowed here.
function AssistConsent({ roomCode }: { roomCode: string }) {
  const socket = useMemo(() => getSocket(), []);
  const [requestBy, setRequestBy] = useState<string | null>(null);
  const [activeAdmin, setActiveAdmin] = useState<string | null>(null);

  useEffect(() => {
    const onReq = ({ adminName }: { adminName: string }) => setRequestBy(adminName);
    const onState = ({ granted, adminName }: { granted: boolean; adminName?: string }) => {
      setRequestBy(null);
      setActiveAdmin(granted ? adminName ?? 'Support' : null);
    };
    socket.on('assist:requested', onReq);
    socket.on('assist:state', onState);
    return () => {
      socket.off('assist:requested', onReq);
      socket.off('assist:state', onState);
    };
  }, [socket]);

  const decide = (allow: boolean) => {
    socket.emit('assist:decide', { roomCode, allow });
    if (!allow) setRequestBy(null);
  };

  if (requestBy) {
    return (
      <section className="card stack" style={{ borderColor: 'var(--accent, #22d3ee)', borderWidth: 2 }}>
        <strong>🎧 {requestBy} (Support) is requesting control of your show.</strong>
        <p className="muted" style={{ margin: 0, fontSize: '0.85rem' }}>
          If you allow it, they can run the console — transport, cues, timers, layout and script —
          alongside you. You can revoke it at any time.
        </p>
        <div className="row">
          <button className="primary" onClick={() => decide(true)}>Allow control</button>
          <button className="danger" onClick={() => decide(false)}>Deny</button>
        </div>
      </section>
    );
  }
  if (activeAdmin) {
    return (
      <section className="card row" style={{ justifyContent: 'space-between', borderColor: 'var(--accent, #22d3ee)' }}>
        <span>🎧 <strong>{activeAdmin} (Support)</strong> has control of your show.</span>
        <button className="danger" onClick={() => decide(false)}>Revoke control</button>
      </section>
    );
  }
  return null;
}

// ---------------------------------------------------------------------------
// The operating surface shared by the producer console and the super-admin
// assist view (when the producer has granted control). Parametrised by an `ops`
// object so it can be backed by either the producer's session actions or the
// admin's direct socket emits.
export interface ControlOps {
  scroll: (control: ScrollControl) => void;
  showCue: (message: InfoMessage) => void;
  clearCue: () => void;
  timer: (control: TimerControl) => void;
  setLayout: (payload: LayoutSet) => void;
  updateSettings: (patch: SettingsUpdate) => void;
}

export function ProducerControls({ session, ops }: { session: SessionState; ops: ControlOps }) {
  const { project, scroll, layout, settings, timers, message } = session;
  return (
    <>
      <Transport scroll={scroll} project={project} onScroll={ops.scroll} />
      <PresenterViewPanel
        project={project}
        anchorBlockId={scroll.anchor.blockId}
        onJump={(blockId) => ops.scroll({ action: 'goto', blockId })}
      />
      <CuePad onCue={ops.showCue} onClear={ops.clearCue} activeMessage={message?.body ?? null} />
      <MessageComposer onCue={ops.showCue} onClear={ops.clearCue} />
      <TimersPanel timers={timers} onTimer={ops.timer} />
      <LayoutPanel currentPreset={layout.presetId} onLayout={(presetId) => ops.setLayout({ presetId })} />
      <DisplayPanel settings={settings} onSettings={ops.updateSettings} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Presenter view — exactly what the talent is reading, with the live reading line
// marked. Click any line to send the presenter there (re-align a lost talent).
function PresenterViewPanel({
  project,
  anchorBlockId,
  onJump,
  fill = false,
}: {
  project: Project;
  anchorBlockId: string;
  onJump: (blockId: string) => void;
  /** Hero mode: grow to match the script editor's height beside it. */
  fill?: boolean;
}) {
  return (
    <section className="card stack">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h3 style={{ margin: 0 }}>Presenter view</h3>
        <span className="muted" style={{ fontSize: '0.75rem' }}>▶ where they're reading · click a line to reposition</span>
      </div>
      <ScriptMirror project={project} anchorBlockId={anchorBlockId} onJump={onJump} fill={fill} maxHeight="42vh" />
    </section>
  );
}

// ---------------------------------------------------------------------------
function Transport({
  scroll,
  project,
  onScroll,
}: {
  scroll: ScrollState;
  project: Project;
  onScroll: (control: ScrollControl) => void;
}) {
  const s = scroll;
  const blocks = flattenBlocks(project);
  const idx = Math.max(0, blocks.findIndex((b) => b.id === s.anchor.blockId));

  return (
    <section className="card stack">
      <h3>Transport</h3>
      <div className="transport">
        {/* Row 1: primary + "advance/more" actions */}
        <button className="primary" onClick={() => onScroll({ action: 'toggle' })}>
          {s.running ? '⏸ Pause' : '▶ Play'}
        </button>
        <button onClick={() => onScroll({ action: 'speedUp' })}>➕ Faster</button>
        <button onClick={() => onScroll({ action: 'nudge', value: 0.5 })}>↓ Forward</button>
        <button onClick={() => onScroll({ action: 'sectionNext' })}>⤓ Next section</button>
        {/* Row 2: return + "reduce/back" actions (each below its pair) */}
        <button onClick={() => onScroll({ action: 'top' })}>⇱ Top</button>
        <button onClick={() => onScroll({ action: 'speedDown' })}>➖ Slower</button>
        <button onClick={() => onScroll({ action: 'nudge', value: -0.5 })}>↑ Back</button>
        <button onClick={() => onScroll({ action: 'sectionPrev' })}>⤒ Prev section</button>
      </div>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <span className="tag readout">{Math.round(s.speedPxPerSec)} px/s</span>
        <span className="tag readout">
          Block {idx + 1}/{blocks.length}
        </span>
        <span className="tag readout">{s.running ? 'Running' : 'Paused'}</span>
      </div>
      <label className="stack" style={{ gap: '0.25rem' }}>
        <span className="muted">Speed</span>
        <input
          type="range"
          min={0}
          max={300}
          value={Math.round(s.speedPxPerSec)}
          onChange={(e) => onScroll({ action: 'setSpeed', value: Number(e.target.value) })}
        />
      </label>
    </section>
  );
}

// ---------------------------------------------------------------------------
function CuePad({
  onCue,
  onClear,
  activeMessage,
}: {
  onCue: (m: InfoMessage) => void;
  onClear: () => void;
  activeMessage: string | null;
}) {
  return (
    <section className="card stack">
      <h3>Quick cues</h3>
      <div className="cue-pad">
        {QUICK_CUES.map((cue) => (
          <button
            key={cue.key}
            onClick={() => {
              const body = cue.body || window.prompt(`${cue.label}:`) || '';
              // Cues with no default body prompt for text (Next question, Hot fact,
              // Video next) — cancel/empty means don't send.
              if (!body && !cue.body) return;
              onCue(
                makeMessage({
                  type: cue.type,
                  heading: cue.heading,
                  body: body || cue.label,
                  ttlMs: cue.ttlMs,
                  colour: cue.colour,
                }),
              );
            }}
          >
            {cue.label}
          </button>
        ))}
      </div>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <span className="muted" style={{ fontSize: '0.85rem' }}>
          {activeMessage ? `On screen: ${activeMessage}` : 'Nothing on screen'}
        </span>
        <button className="danger" onClick={onClear}>
          Clear screen
        </button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
function MessageComposer({
  onCue,
  onClear,
}: {
  onCue: (m: InfoMessage) => void;
  onClear: () => void;
}) {
  const [type, setType] = useState<InfoMessageType>('message');
  const [heading, setHeading] = useState('');
  const [body, setBody] = useState('');
  const [ttl, setTtl] = useState(0);

  return (
    <section className="card stack">
      <h3>Message to screen</h3>
      <div className="row">
        <select value={type} onChange={(e) => setType(e.target.value as InfoMessageType)} aria-label="Message type">
          <option value="message">Note</option>
          <option value="question">Question</option>
          <option value="fact">Hot fact</option>
          <option value="cue">Cue</option>
          <option value="timer">Timing</option>
          <option value="urgent">Urgent</option>
        </select>
        <input
          placeholder="Heading (optional)"
          value={heading}
          onChange={(e) => setHeading(e.target.value)}
          aria-label="Message heading"
          style={{ flex: 1 }}
        />
      </div>
      <textarea
        placeholder="Body text shown to the presenter"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        aria-label="Message body"
        rows={2}
      />
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <label className="row" style={{ gap: '0.35rem' }}>
          <span className="muted">Auto-clear</span>
          <select value={ttl} onChange={(e) => setTtl(Number(e.target.value))} aria-label="Auto-clear after">
            <option value={0}>Off</option>
            <option value={5000}>5s</option>
            <option value={10000}>10s</option>
            <option value={30000}>30s</option>
          </select>
        </label>
        <div className="row">
          <button className="danger" onClick={onClear}>
            Clear
          </button>
          <button
            className="primary"
            disabled={!body.trim()}
            onClick={() => onCue(makeMessage({ type, heading, body, ttlMs: ttl || undefined }))}
          >
            Send to screen
          </button>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
function TimersPanel({ timers, onTimer }: { timers: Timers; onTimer: (control: TimerControl) => void }) {
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 500);
    return () => clearInterval(t);
  }, []);
  const now = Date.now();
  return (
    <section className="card stack">
      <h3>Timers</h3>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <span>Production (up)</span>
        <span className="tag readout">{formatTimer(timerValueMs(timers.production, now))}</span>
      </div>
      <div className="row">
        <button onClick={() => onTimer({ which: 'production', action: 'start' })}>Start</button>
        <button onClick={() => onTimer({ which: 'production', action: 'pause' })}>Pause</button>
        <button onClick={() => onTimer({ which: 'production', action: 'reset' })}>Reset</button>
        <button onClick={() => onTimer({ which: 'production', action: timers.production.visible ? 'hide' : 'show' })}>
          {timers.production.visible ? 'Hide' : 'Show'}
        </button>
      </div>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <span>Segment (down)</span>
        <span className="tag readout">{formatTimer(timerValueMs(timers.segment, now))}</span>
      </div>
      <div className="row">
        <button onClick={() => onTimer({ which: 'segment', action: 'start' })}>Start</button>
        <button onClick={() => onTimer({ which: 'segment', action: 'pause' })}>Pause</button>
        <button onClick={() => onTimer({ which: 'segment', action: 'reset' })}>Reset</button>
        <select
          aria-label="Segment duration"
          value={timers.segment.durationMs}
          onChange={(e) => onTimer({ which: 'segment', action: 'setDuration', value: Number(e.target.value) })}
        >
          <option value={60000}>1 min</option>
          <option value={120000}>2 min</option>
          <option value={300000}>5 min</option>
          <option value={600000}>10 min</option>
        </select>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
function LayoutPanel({
  currentPreset,
  onLayout,
}: {
  currentPreset: LayoutPresetId;
  onLayout: (id: LayoutPresetId) => void;
}) {
  return (
    <section className="card stack">
      <h3>Layout</h3>
      <div className="chip-row">
        {(Object.keys(LAYOUT_PRESETS) as LayoutPresetId[]).map((id) => (
          <button
            key={id}
            className={id === currentPreset ? 'primary' : ''}
            onClick={() => onLayout(id)}
          >
            {LAYOUT_PRESETS[id].label}
          </button>
        ))}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
function DisplayPanel({
  settings,
  onSettings,
}: {
  settings: DisplaySettings;
  onSettings: (patch: SettingsUpdate) => void;
}) {
  return (
    <section className="card stack">
      <h3>Presenter display</h3>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <span className="muted">Font size {settings.fontSizePx}px</span>
        <div className="row">
          <button onClick={() => onSettings({ fontSizePx: Math.max(16, settings.fontSizePx - 4) })}>A−</button>
          <button onClick={() => onSettings({ fontSizePx: Math.min(180, settings.fontSizePx + 4) })}>A+</button>
        </div>
      </div>
      <label className="stack" style={{ gap: '0.25rem' }}>
        <span className="muted">Reading line position</span>
        <input
          type="range"
          min={0}
          max={100}
          value={Math.round(settings.readingBandPos * 100)}
          onChange={(e) => onSettings({ readingBandPos: Number(e.target.value) / 100 })}
        />
      </label>
      <div className="row">
        <button onClick={() => onSettings({ mirrorHorizontal: !settings.mirrorHorizontal })}>
          Mirror H: {settings.mirrorHorizontal ? 'on' : 'off'}
        </button>
        <button onClick={() => onSettings({ uppercase: !settings.uppercase })}>
          Uppercase: {settings.uppercase ? 'on' : 'off'}
        </button>
        <select
          aria-label="Contrast"
          value={settings.contrast}
          onChange={(e) => onSettings({ contrast: e.target.value as 'standard' | 'high' | 'soft' })}
        >
          <option value="standard">Standard</option>
          <option value="high">High contrast</option>
          <option value="soft">Soft</option>
        </select>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// AI panel — calls the SERVER mock (keys stay server-side). Every suggestion is
// labelled and requires the producer to approve it onto the screen.
// ---------------------------------------------------------------------------
type AiSuggestion = { id: string; kind: string; text: string; generated: true; needsVerification: boolean };

function AiPanel({ project, onCue }: { project: Project; onCue: (m: InfoMessage) => void }) {
  const [want, setWant] = useState('followUpQuestion');
  const [busy, setBusy] = useState(false);
  const [suggestions, setSuggestions] = useState<AiSuggestion[]>([]);
  const context = useMemo(() => flattenBlocks(project).map((b) => b.text).join(' ').slice(-500), [project]);

  const ask = async () => {
    setBusy(true);
    try {
      const res = await fetch('/ai/suggest', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ text: context, want }),
      });
      const data = (await res.json()) as { suggestions: AiSuggestion[] };
      setSuggestions(data.suggestions ?? []);
    } catch {
      setSuggestions([]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card stack">
      <h3>AI producer (sample)</h3>
      <p className="muted" style={{ margin: 0, fontSize: '0.85rem' }}>
        Suggestions are AI-generated samples — review before sending. Nothing reaches the presenter
        without your approval.
      </p>
      <div className="row">
        <select value={want} onChange={(e) => setWant(e.target.value)} aria-label="AI request">
          <option value="followUpQuestion">Follow-up question</option>
          <option value="hotFact">Hot fact</option>
          <option value="shorten">Shorten</option>
          <option value="rewrite">Rewrite</option>
        </select>
        <button disabled={busy} onClick={ask}>
          {busy ? 'Thinking…' : 'Suggest'}
        </button>
      </div>
      <div className="stack">
        {suggestions.map((sug) => (
          <div key={sug.id} className="row" style={{ justifyContent: 'space-between', gap: '0.5rem' }}>
            <span>
              <span className="tag" style={{ marginRight: '0.4rem' }}>
                AI{sug.needsVerification ? ' · verify' : ''}
              </span>
              {sug.text}
            </span>
            <button
              onClick={() =>
                onCue(
                  makeMessage({
                    type: sug.kind === 'hotFact' ? 'fact' : sug.kind === 'followUpQuestion' ? 'question' : 'message',
                    body: sug.text,
                  }),
                )
              }
            >
              Send →
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
function ProjectsPanel({ project, onLoad }: { project: Project; onLoad: (p: Project) => void }) {
  const [saved, setSaved] = useState<Project[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);

  const refresh = () => listProjects().then(setSaved);
  useEffect(() => {
    void refresh();
  }, []);

  const download = (name: string, content: string, mime: string) => {
    const url = URL.createObjectURL(new Blob([content], { type: mime }));
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
  };

  const importFile = async (file: File) => {
    const text = await file.text();
    if (file.name.endsWith('.json')) {
      try {
        const parsed = ProjectSchema.parse(JSON.parse(text));
        onLoad(parsed);
      } catch {
        window.alert('That JSON is not a valid project file.');
      }
    } else {
      onLoad(parseScript(text, file.name.replace(/\.[^.]+$/, '')));
    }
  };

  return (
    <section className="card stack">
      <h3>Projects (local)</h3>
      <div className="row">
        <button
          onClick={async () => {
            await saveProject(project);
            await refresh();
          }}
        >
          Save
        </button>
        <button onClick={() => download(`${project.title || 'script'}.json`, JSON.stringify(project, null, 2), 'application/json')}>
          Export JSON
        </button>
        <button onClick={() => download(`${project.title || 'script'}.txt`, serializeScript(project), 'text/plain')}>
          Export text
        </button>
        <button onClick={() => fileRef.current?.click()}>Import…</button>
        <input
          ref={fileRef}
          type="file"
          accept=".json,.txt,text/plain,application/json"
          className="visually-hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void importFile(f);
            e.target.value = '';
          }}
        />
      </div>
      {saved.length > 0 && (
        <div className="stack">
          {saved.map((p) => (
            <div key={p.id} className="row" style={{ justifyContent: 'space-between' }}>
              <span>{p.title}</span>
              <button onClick={() => onLoad(p)}>Load</button>
            </div>
          ))}
        </div>
      )}
      <button
        className="danger"
        onClick={async () => {
          if (window.confirm('Delete all local projects and settings on this device?')) {
            await deleteAllLocalData();
            await refresh();
          }
        }}
      >
        Delete local data
      </button>
    </section>
  );
}
